import { type ReviewChecklistItem } from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CheckCircle,
  CircleNotch,
  ClockCountdown,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";

interface ChecklistItem {
  id: string;
  label: string;
  sub: string;
}

const CHECKLIST_BY_WORKFLOW: Record<string, ChecklistItem[]> = {
  artwork_review: [
    {
      id: "file_build",
      label: "File Build",
      sub: "Dielines, fonts, colors, metadata, production setup",
    },
    {
      id: "design_layout",
      label: "Design & Layout",
      sub: "Logo, brand guidelines, imagery, barcode, print readiness",
    },
    {
      id: "content_claims",
      label: "Content & Claims",
      sub: "Ingredients, nutrition facts, regulatory text, disclaimers",
    },
    {
      id: "master_comparison",
      label: "Master Comparison",
      sub: "Alignment to reference spec, dimensions, copy accuracy",
    },
  ],
  compliance_review: [
    {
      id: "regulatory",
      label: "Regulatory Compliance",
      sub: "EU/UK regulations, permitted health and nutrition claims",
    },
    {
      id: "claim_accuracy",
      label: "Claim Accuracy",
      sub: "Evidence base, substantiation requirements",
    },
    {
      id: "market_rules",
      label: "Market-specific Rules",
      sub: "Regional and country-level requirements",
    },
    {
      id: "enforcement",
      label: "Enforcement Risk",
      sub: "ASA, FSA, and regulator precedent cases",
    },
  ],
  compare: [
    {
      id: "design_align",
      label: "Design Alignment",
      sub: "Layout, colors, typography, visual hierarchy",
    },
    {
      id: "content_match",
      label: "Content Accuracy",
      sub: "Copy, claims, specifications against reference",
    },
    {
      id: "brand_consistency",
      label: "Brand Consistency",
      sub: "Logo usage, color palette, brand standards",
    },
    {
      id: "spec_compliance",
      label: "Specification Compliance",
      sub: "Dimensions, materials, technical requirements",
    },
  ],
  review: [
    {
      id: "brand",
      label: "Brand Guidelines",
      sub: "Visual standards, identity, tone of voice",
    },
    {
      id: "visual_quality",
      label: "Visual Quality",
      sub: "Composition, imagery, typography, design",
    },
    {
      id: "content",
      label: "Content Review",
      sub: "Copy, claims, messaging, accuracy",
    },
    {
      id: "technical",
      label: "Technical Specifications",
      sub: "File format, resolution, print requirements",
    },
  ],
};

const FALLBACK_CHECKLIST: ChecklistItem[] = [
  {
    id: "analysis",
    label: "Analysing files",
    sub: "Reading and processing uploaded assets",
  },
  {
    id: "review",
    label: "Running review",
    sub: "Applying knowledge base and guidelines",
  },
  {
    id: "findings",
    label: "Compiling findings",
    sub: "Generating structured feedback and results",
  },
];

function toChecklistItems(
  dynamic: ReviewChecklistItem[] | undefined,
  workflowType: string,
): ChecklistItem[] {
  if (dynamic && dynamic.length > 0) {
    return dynamic.map((item, idx) => ({
      id: `dynamic_${idx}`,
      label: item.label,
      sub: item.sub,
    }));
  }
  return CHECKLIST_BY_WORKFLOW[workflowType] ?? FALLBACK_CHECKLIST;
}

interface ReviewChecklistProps {
  workflowType: string;
  isReviewPolling?: boolean;
  reviewComplete?: boolean;
  dynamicItems?: ReviewChecklistItem[];
}

export function ReviewChecklist({
  workflowType,
  isReviewPolling = false,
  reviewComplete = false,
  dynamicItems,
}: ReviewChecklistProps) {
  const items = toChecklistItems(dynamicItems, workflowType);

  const [widgetOpen, setWidgetOpen] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const hasDynamic = !!(dynamicItems && dynamicItems.length > 0);
  const staggerKey = `${hasDynamic ? "dyn" : "fallback"}-${items.length}`;

  // Refs survive remounts. Store the wall-clock start time and pre-computed
  // cumulative delays so a component remount (waitingForAI→false moves the
  // element in the DOM) can resume exactly where the timer left off.
  const startTimeRef = useRef<number | null>(null);
  const staggerDelaysRef = useRef<number[]>([]);
  const prevStaggerKeyRef = useRef("");

  if (prevStaggerKeyRef.current !== staggerKey && !reviewComplete) {
    prevStaggerKeyRef.current = staggerKey;
    startTimeRef.current = Date.now();
    const delays: number[] = [0];
    for (let i = 1; i < items.length; i++) {
      delays.push(delays[i - 1] + 3000 + Math.random() * 4000);
    }
    staggerDelaysRef.current = delays;
  }

  // On remount, initialize visibleCount to however many items have already
  // elapsed — so we never flash backwards or pre-fill the box.
  const [visibleCount, setVisibleCount] = useState<number>(() => {
    if (reviewComplete) return items.length;
    const start = startTimeRef.current;
    if (start === null) return 0;
    const elapsed = Date.now() - start;
    return staggerDelaysRef.current.filter((d) => d <= elapsed).length;
  });

  // Collapse the widget automatically when the review finishes.
  useEffect(() => {
    if (reviewComplete) setWidgetOpen(false);
  }, [reviewComplete]);

  useEffect(() => {
    if (reviewComplete) {
      setVisibleCount(items.length);
      return;
    }

    // Recompute how many items should be visible right now (handles remounts).
    const start = startTimeRef.current ?? Date.now();
    const delays = staggerDelaysRef.current;
    const nowCount = delays.filter((d) => start + d <= Date.now()).length;
    setVisibleCount(nowCount);

    let count = nowCount;
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      if (count >= items.length) return;
      const remaining = start + delays[count] - Date.now();
      timeoutId = setTimeout(
        () => {
          count++;
          setVisibleCount(count);
          scheduleNext();
        },
        Math.max(0, remaining),
      );
    };

    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, [staggerKey, reviewComplete, items.length]);

  const toggleItem = (itemId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const done = isReviewPolling || reviewComplete;
  const visibleItems = items.slice(0, visibleCount);

  const MAX_COLLAPSED = 5;
  const [showAll, setShowAll] = useState(false);
  const displayedItems = showAll
    ? visibleItems
    : visibleItems.slice(0, MAX_COLLAPSED);
  const hasMore = visibleItems.length > MAX_COLLAPSED;

  return (
    <div
      data-testid="review-checklist"
      className="rounded-xl border border-puntt-neutral-gray-4 bg-base-white shadow-sm"
    >
      {/* Header — click to collapse/expand the whole widget */}
      <button
        onClick={() => setWidgetOpen((p) => !p)}
        className="flex w-full items-center gap-2 px-4 py-3"
      >
        {isReviewPolling ? (
          <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-accent-9" />
        ) : reviewComplete ? (
          <CheckCircle size={16} weight="fill" className="text-malachite-600" />
        ) : (
          <CircleNotch
            size={16}
            className="animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          />
        )}
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          {isReviewPolling
            ? "Review in progress"
            : reviewComplete
              ? "Review complete"
              : "Reviewing your files"}
        </Text>
        {isReviewPolling && (
          <Flex align="center" gap="1" className="mr-1">
            <ClockCountdown size={12} className="text-puntt-neutral-gray-9" />
            <Text size="1" className="text-puntt-neutral-gray-9">
              Results on ticket
            </Text>
          </Flex>
        )}
        <CaretDown
          size={14}
          className={`ml-auto shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200 ${widgetOpen ? "rotate-0" : "-rotate-90"}`}
        />
      </button>

      {/* Items — only render what's visible so the box grows as items appear */}
      {widgetOpen && displayedItems.length > 0 && (
        <div className="border-t border-puntt-neutral-gray-4 px-2 pb-2 pt-1">
          {displayedItems.map((item, idx) => {
            const isLatestEntry =
              idx === visibleItems.length - 1 && !reviewComplete;
            const itemExpanded = expandedIds.has(item.id);

            return (
              <div
                key={item.id}
                className={isLatestEntry ? "animate-block-fade-up" : undefined}
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
                >
                  {done ? (
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className="mt-0.5 shrink-0 text-malachite-600"
                    />
                  ) : (
                    <CircleNotch
                      size={16}
                      className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
                      style={{ animationDuration: `${1.5 + idx * 0.3}s` }}
                    />
                  )}
                  <Text
                    size="2"
                    weight="medium"
                    className={`flex-1 text-left ${
                      done
                        ? "text-puntt-neutral-gray-11"
                        : "text-puntt-neutral-gray-12"
                    }`}
                  >
                    {item.label}
                  </Text>
                  <CaretDown
                    size={12}
                    className={`shrink-0 text-puntt-neutral-gray-7 transition-transform duration-150 ${itemExpanded ? "rotate-0" : "-rotate-90"}`}
                  />
                </button>

                {itemExpanded && (
                  <Text
                    as="p"
                    size="1"
                    className={`pb-1 pl-9 pr-2 ${done ? "text-puntt-neutral-gray-8" : "text-puntt-neutral-gray-9"}`}
                  >
                    {item.sub}
                  </Text>
                )}
              </div>
            );
          })}

          {hasMore && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAll((p) => !p);
              }}
              className="mt-1 w-full rounded-lg px-2 py-1.5 text-left hover:bg-puntt-neutral-gray-2"
            >
              <Text size="1" className="text-puntt-neutral-gray-9">
                {showAll
                  ? "Show less"
                  : `Show ${visibleItems.length - MAX_COLLAPSED} more`}
              </Text>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
